/**
 * Merged-buffer builder for the WebGL2 main pass (map-FPS perf Stages 2+3).
 *
 * Stage 2 — batching: concatenates per-element vertex data into one
 * CPU-side `Float32Array` per element-type bucket so the renderer's main
 * pass issues tens of `drawArrays` calls instead of ~1 900 per-element
 * ones. Pure CPU module — the renderer uploads each part into its own VBO.
 *
 * Stage 3 — tile culling (web variant A): the buckets in
 * {@link TILED_BUCKETS} (stations + additionals) are additionally split by
 * the 8×8/512-px tile grid (`tileGrid.ts`): each element goes to exactly
 * ONE tile — the one containing its AABB center — or to the bucket's
 * `global` part when oversized / without an AABB. Per frame the renderer
 * draws a bucket's `global` part first, then its VISIBLE tiles ascending.
 * Texts are not in the GL element set (labels are the SVG overlay), and
 * connections + transitions deliberately stay whole Stage-2 merged buffers
 * in `global` (their long strokes span many tiles — tiling them would
 * push nearly everything into `global` anyway).
 *
 * Order preservation (the #1 correctness risk — painter's algorithm, no
 * depth buffer): the input `GeometrySet.elements` array is already
 * stable-sorted by `renderFloor`, and the `RenderFloor` bands are DISJOINT
 * per element type (additional 0 < connections 100–170 < station 300 <
 * transition 400 < text 500), so grouping consecutive elements by band and
 * drawing buckets in first-seen order reproduces the flat per-element
 * iteration order. Within an element, fill vertices precede stroke
 * vertices — exactly the old per-element fill-before-stroke draw order.
 * Within a tiled bucket the cross-platform frame order is global-first,
 * then tiles ascending, with source order inside each part.
 *
 * Exclusion: an element whose id is not in the `visibleIds` allow-set (the
 * night-service filter) is simply not concatenated — merged buffers are
 * REBUILT on night-set / theme change (rare user-level events), never per
 * frame.
 *
 * Blend-state note (plan Task 2.4 first step): the main pass runs with
 * `gl.BLEND` DISABLED — `WebGL2Renderer.draw` enables blending only around
 * the dim-quad pass and disables it again before the highlight pass.
 * Stage 3's one-tile assignment means no duplicate draws, so the finding
 * is moot for correctness but stays documented for future passes.
 */
import { FLOATS_PER_VERTEX, RenderFloor, type ElementGeometry } from '../geometry/types'
import { aabbOfVertices, GLOBAL_TILE, TILE_COUNT, tileIndexForAabb } from './tileGrid'

/** Element-type bucket a `RenderFloor` band maps to — mirrors the iOS/Android per-type buffer key. */
export type MergedBucketName = 'additional' | 'connection' | 'station' | 'transition' | 'text'

/**
 * Buckets that get the Stage-3 tile split (web variant A — see module doc).
 */
export const TILED_BUCKETS: ReadonlySet<MergedBucketName> = new Set(['station', 'additional'])

/**
 * One merged bucket. `global` carries the whole bucket for non-tiled
 * types, and the oversized / no-AABB spill for tiled types; `tiles` has
 * {@link TILE_COUNT} slots (all `null` for non-tiled buckets). Every part
 * concatenates its elements in draw order.
 */
export interface MergedBucketData {
  readonly bucket: MergedBucketName
  readonly global: Float32Array | null
  readonly tiles: ReadonlyArray<Float32Array | null>
}

/** Vertex count of an interleaved 6-float part. */
export function vertexCountOf(vertices: Float32Array): number {
  return vertices.length / FLOATS_PER_VERTEX
}

/**
 * Maps a `RenderFloor` value to its element-type bucket. Band boundaries
 * are the type-block starts in `RenderFloor` — see the order-preservation
 * rationale in the module doc.
 */
export function bucketForRenderFloor(floor: number): MergedBucketName {
  if (floor < RenderFloor.CONNECTION_UNDERGROUND) return 'additional'
  if (floor < RenderFloor.STATION) return 'connection'
  if (floor < RenderFloor.TRANSITION) return 'station'
  if (floor < RenderFloor.TEXT) return 'transition'
  return 'text'
}

/** Internal accumulator for one (bucket, part) target during the two-pass build. */
interface PartBuild {
  vertices: Float32Array
  offset: number
}

/**
 * Builds the merged per-type vertex arrays for the main pass.
 *
 * @param elements - `GeometrySet.elements`, stable-sorted by `renderFloor`
 *   (the provider guarantees this; bucket contiguity follows from the
 *   disjoint floor bands).
 * @param visibleIds - night-service allow-set; `null` = normal mode, every
 *   element is included.
 * @returns buckets in first-seen (= ascending-floor) order; buckets with
 *   zero visible floats are omitted.
 */
export function buildMergedBuckets(
  elements: readonly ElementGeometry[],
  visibleIds: ReadonlySet<string> | null,
  tiled = true,
): MergedBucketData[] {
  // The part an element lands in: GLOBAL_TILE for non-tiled buckets and
  // for oversized / AABB-less elements of tiled buckets; a tile index
  // otherwise. Deterministic per element, so both passes agree.
  const partFor = (el: ElementGeometry, bucket: MergedBucketName): number => {
    if (!tiled || !TILED_BUCKETS.has(bucket)) return GLOBAL_TILE
    return tileIndexForAabb(aabbOfVertices(el.fillVertices, el.strokeVertices))
  }

  // Pass 1 — total float count per (bucket, part), buckets in first-seen order.
  const floatTotals = new Map<MergedBucketName, Map<number, number>>()
  for (const el of elements) {
    if (visibleIds !== null && !visibleIds.has(el.id)) continue
    const floats = el.fillVertices.length + el.strokeVertices.length
    if (floats === 0) continue
    const bucket = bucketForRenderFloor(el.renderFloor)
    let parts = floatTotals.get(bucket)
    if (parts === undefined) {
      parts = new Map<number, number>()
      floatTotals.set(bucket, parts)
    }
    const part = partFor(el, bucket)
    parts.set(part, (parts.get(part) ?? 0) + floats)
  }

  // Pass 2 — allocate once per part and copy in draw order.
  const builds = new Map<MergedBucketName, Map<number, PartBuild>>()
  for (const [bucket, parts] of floatTotals) {
    const targets = new Map<number, PartBuild>()
    for (const [part, total] of parts) {
      targets.set(part, { vertices: new Float32Array(total), offset: 0 })
    }
    builds.set(bucket, targets)
  }
  for (const el of elements) {
    if (visibleIds !== null && !visibleIds.has(el.id)) continue
    if (el.fillVertices.length + el.strokeVertices.length === 0) continue
    const bucket = bucketForRenderFloor(el.renderFloor)
    const target = builds.get(bucket)?.get(partFor(el, bucket))
    if (target === undefined) continue
    if (el.fillVertices.length > 0) {
      target.vertices.set(el.fillVertices, target.offset)
      target.offset += el.fillVertices.length
    }
    if (el.strokeVertices.length > 0) {
      target.vertices.set(el.strokeVertices, target.offset)
      target.offset += el.strokeVertices.length
    }
  }

  const out: MergedBucketData[] = []
  for (const [bucket, targets] of builds) {
    const tiles: Array<Float32Array | null> = new Array<Float32Array | null>(TILE_COUNT).fill(null)
    let global: Float32Array | null = null
    for (const [part, build] of targets) {
      if (part === GLOBAL_TILE) {
        global = build.vertices
      } else {
        tiles[part] = build.vertices
      }
    }
    out.push({ bucket, global, tiles })
  }
  return out
}
